import { useMemo } from 'react';
// Import Enums
import { PageTypesEnum } from 'enums';
// Import Constants
import { l } from 'constants/common';
import { REQUEST_TYPE_OPTIONS, SMS_RESPONSE_REGEX_TYPE_OPTIONS, SMSProviderFieldsEnum } from './constants';

interface UseSMSProviderCECFormInputsOptions {
    mode: PageTypesEnum;
    hasAdminPermission: boolean;
}

export const useSMSProviderCECFormInputs = (options: UseSMSProviderCECFormInputsOptions) => {
    const { mode, hasAdminPermission } = options;
    const disabled = !hasAdminPermission;
    const isCreateEditDisabledForNonAdmins = [PageTypesEnum.create, PageTypesEnum.edit].includes(mode) && disabled;

    const fieldsList = useMemo(
        () =>
            Object.values({
                top: {
                    title: 'General',
                    leftFields: [
                        {
                            name: SMSProviderFieldsEnum.PartnerId,
                            type: 'virtualizedDropdown',
                            disabled: isCreateEditDisabledForNonAdmins,
                        },
                        {
                            name: SMSProviderFieldsEnum.Url,
                            label: l.Url,
                            labelAppearance: 'swap',
                            type: 'text',
                            disabled: isCreateEditDisabledForNonAdmins,
                        },
                        {
                            name: SMSProviderFieldsEnum.ApiKey,
                            label: l.ApiKey,
                            labelAppearance: 'swap',
                            type: 'text',
                            disabled: isCreateEditDisabledForNonAdmins,
                        },
                        {
                            name: SMSProviderFieldsEnum.Name,
                            label: l.Name,
                            labelAppearance: 'swap',
                            type: 'text',
                            disabled: isCreateEditDisabledForNonAdmins,
                        },
                        {
                            name: SMSProviderFieldsEnum.Description,
                            label: l.Description,
                            labelAppearance: 'swap',
                            type: 'text',
                            disabled: isCreateEditDisabledForNonAdmins,
                        },
                        {
                            name: SMSProviderFieldsEnum.FromAddress,
                            label: l.FromAddress,
                            labelAppearance: 'swap',
                            type: 'text',
                            disabled: isCreateEditDisabledForNonAdmins,
                        },
                        {
                            name: SMSProviderFieldsEnum.PostMessageBody,
                            label: l.PostMessageBody,
                            labelAppearance: 'swap',
                            type: 'textarea',
                            disabled,
                        },
                    ],
                    rightFields: [
                        {
                            name: SMSProviderFieldsEnum.UserName,
                            label: l.UserName,
                            labelAppearance: 'swap',
                            type: 'text',
                            disabled: isCreateEditDisabledForNonAdmins,
                        },
                        {
                            name: SMSProviderFieldsEnum.Password,
                            label: l.Password,
                            labelAppearance: 'swap',
                            type: 'text',
                            disabled: isCreateEditDisabledForNonAdmins,
                        },
                        {
                            name: SMSProviderFieldsEnum.RequestType,
                            label: l.RequestType,
                            labelAppearance: 'swap',
                            type: 'dropdown',
                            data: REQUEST_TYPE_OPTIONS,
                            disabled,
                        },
                        {
                            name: SMSProviderFieldsEnum.MediaType,
                            label: l.MediaType,
                            labelAppearance: 'swap',
                            type: 'text',
                            disabled,
                        },
                        {
                            name: SMSProviderFieldsEnum.MaxDegreeOfParallelism,
                            label: l.MaxDegreeOfParallelism,
                            labelAppearance: 'swap',
                            type: 'number',
                            min: 1,
                            max: 10,
                            disabled,
                        },
                        {
                            name: SMSProviderFieldsEnum.DelayTimeout,
                            label: l.DelayTimeout,
                            labelAppearance: 'swap',
                            type: 'number',
                            min: 0,
                            max: 10000,
                            disabled,
                        },
                        {
                            name: SMSProviderFieldsEnum.Note,
                            label: l.Note,
                            labelAppearance: 'swap',
                            type: 'textarea',
                            disabled,
                        },
                    ],
                },
                bottomLeft: {
                    title: SMSProviderFieldsEnum.ResponseRegex,
                    fields: [
                        {
                            name: SMSProviderFieldsEnum.ResponseRegex,
                            type: 'block',
                            fields: [
                                {
                                    name: `${SMSProviderFieldsEnum.ResponseRegex}.${SMSProviderFieldsEnum.Type}`,
                                    label: l.Type,
                                    labelAppearance: 'swap',
                                    type: 'dropdown',
                                    data: SMS_RESPONSE_REGEX_TYPE_OPTIONS,
                                    disabled,
                                },
                                {
                                    name: SMSProviderFieldsEnum.Properties,
                                    label: l.Properties,
                                    type: 'nestedBlock',
                                    fields: [
                                        {
                                            name: `${SMSProviderFieldsEnum.ResponseRegex}.${SMSProviderFieldsEnum.Properties}.${SMSProviderFieldsEnum.ReplaceBy}`,
                                            label: l.ReplaceBy,
                                            labelAppearance: 'swap',
                                            type: 'text',
                                            disabled,
                                        },
                                        {
                                            name: `${SMSProviderFieldsEnum.ResponseRegex}.${SMSProviderFieldsEnum.Properties}.${SMSProviderFieldsEnum.ErrorCode}`,
                                            label: l.ErrorCode,
                                            labelAppearance: 'swap',
                                            type: 'text',
                                            disabled,
                                        },
                                        {
                                            name: `${SMSProviderFieldsEnum.ResponseRegex}.${SMSProviderFieldsEnum.Properties}.${SMSProviderFieldsEnum.ErrorMessage}`,
                                            label: l.ErrorMessage,
                                            labelAppearance: 'swap',
                                            type: 'text',
                                            disabled,
                                        },
                                        {
                                            name: `${SMSProviderFieldsEnum.ResponseRegex}.${SMSProviderFieldsEnum.Properties}.${SMSProviderFieldsEnum.ExternalId}`,
                                            label: l.ExternalId,
                                            labelAppearance: 'swap',
                                            type: 'text',
                                            disabled,
                                        },
                                        {
                                            name: `${SMSProviderFieldsEnum.ResponseRegex}.${SMSProviderFieldsEnum.Properties}.${SMSProviderFieldsEnum.DeliveryStatus}`,
                                            label: l.DeliveryStatus,
                                            labelAppearance: 'swap',
                                            type: 'text',
                                            disabled,
                                        },
                                    ],
                                },
                                {
                                    name: SMSProviderFieldsEnum.Error,
                                    label: l.Error,
                                    type: 'nestedBlock',
                                    fields: [
                                        {
                                            name: `${SMSProviderFieldsEnum.ResponseRegex}.${SMSProviderFieldsEnum.Error}.${SMSProviderFieldsEnum.ReplaceBy}`,
                                            label: l.ReplaceBy,
                                            labelAppearance: 'swap',
                                            type: 'text',
                                            disabled,
                                        },
                                        {
                                            name: `${SMSProviderFieldsEnum.ResponseRegex}.${SMSProviderFieldsEnum.Error}.${SMSProviderFieldsEnum.ErrorCode}`,
                                            label: l.ErrorCode,
                                            labelAppearance: 'swap',
                                            type: 'text',
                                            disabled,
                                        },
                                        {
                                            name: `${SMSProviderFieldsEnum.ResponseRegex}.${SMSProviderFieldsEnum.Error}.${SMSProviderFieldsEnum.ErrorMessage}`,
                                            label: l.ErrorMessage,
                                            labelAppearance: 'swap',
                                            type: 'text',
                                            disabled,
                                        },
                                        {
                                            name: `${SMSProviderFieldsEnum.ResponseRegex}.${SMSProviderFieldsEnum.Error}.${SMSProviderFieldsEnum.ExternalId}`,
                                            label: l.ExternalId,
                                            labelAppearance: 'swap',
                                            type: 'text',
                                            disabled,
                                        },
                                        {
                                            name: `${SMSProviderFieldsEnum.ResponseRegex}.${SMSProviderFieldsEnum.Error}.${SMSProviderFieldsEnum.DeliveryStatus}`,
                                            label: l.DeliveryStatus,
                                            labelAppearance: 'swap',
                                            type: 'text',
                                            disabled,
                                        },
                                    ],
                                },
                                {
                                    name: `${SMSProviderFieldsEnum.ResponseRegex}.${SMSProviderFieldsEnum.SuccessCode}`,
                                    label: l.SuccessCode,
                                    labelAppearance: 'swap',
                                    type: 'number',
                                    disabled,
                                },
                                {
                                    name: `${SMSProviderFieldsEnum.ResponseRegex}.${SMSProviderFieldsEnum.DefaultCode}`,
                                    label: l.DefaultCode,
                                    labelAppearance: 'swap',
                                    type: 'number',
                                    disabled,
                                },
                                {
                                    name: `${SMSProviderFieldsEnum.ResponseRegex}.${SMSProviderFieldsEnum.CodeMap}`,
                                    label: l.CodeMap,
                                    type: 'json',
                                    disabled,
                                    shouldNotBeNested: true,
                                    fieldValueType: 'number',
                                },
                            ],
                        },
                    ],
                },
                bottomRight: {
                    title: SMSProviderFieldsEnum.AdditionalFields,
                    fields: [
                        {
                            name: SMSProviderFieldsEnum.AdditionalFields,
                            type: 'json',
                            fieldValueType: 'text',
                            disabled,
                        },
                    ],
                },
            }),
        [disabled, isCreateEditDisabledForNonAdmins],
    );

    return { fieldsList };
};
